import { ApolloClient, InMemoryCache } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';
import { TEAM_REVEAL_WORDPRESS_CATEGORY } from '@lib/constants';

const client = new ApolloClient({
  uri:
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
      ? process.env.NEXT_PUBLIC_GRAPH_CDN_URL
      : process.env.NEXT_PUBLIC_WORDPRESS_API_URL,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          //@ts-ignore
          keyArgs: false,
          posts: relayStylePagination()
        }
      },
      PostTypeSEO: {
        keyFields: ['opengraphUrl']
      }
    }
  })
});

const getArticlesByAuthorNameQuery = (
  name
) => `query GetAuthorByName($cursor: String) {
    posts(first: 10, after: $cursor, where: {authorName: "${name}", orderby: {field: DATE, order: DESC}}) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
            startCursor
          }
          edges {
            node {
              id
              date
              title
              excerpt(format: RENDERED)
              slug
              author {
                node {
                  id
                  name
                  uri
                  slug
                  avatar {
                    url
                  }
                }
              }
              featuredImage {
                node {
                  id
                  sourceUrl(size: LARGE)
                  altText
                  mediaDetails {
                    height
                    width
                  }
                }
              }
              seo {
                readingTime
                opengraphUrl
              }
              primaryCategory
            }
          }
        }
      }`;

const getTeamRevealsByAuthorNameQuery = (
  name
) => `query GetAuthorByName($cursor: String) {
    posts(first: 10, after: $cursor, where: {authorName: "${name}", categoryId: ${TEAM_REVEAL_WORDPRESS_CATEGORY}, orderby: {field: DATE, order: DESC}}) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
            startCursor
          }
          edges {
            node {
              id
              date
              title
              excerpt(format: RENDERED)
              slug
              author {
                node {
                  id
                  name
                  uri
                  slug
                  avatar {
                    url
                  }
                }
              }
              featuredImage {
                node {
                  id
                  sourceUrl(size: LARGE)
                  altText
                  mediaDetails {
                    height
                    width
                  }
                }
              }
              seo {
                readingTime
                opengraphUrl
              }
              primaryCategory
            }
          }
        }
      }`;

const getCategoryBySlugQuery = (slug) => `query GetCategoryBySlug {
    categories(where: {slug: "${slug}"}) {
      edges {
        node {
          id
          categoryId
          name
        }
      }
    }
  }    
  `;

const getPostsByCategoryId = (
  categoryId
) => `query GetPostsByCategoryId ($cursor: String) {
    posts(first: 10, after: $cursor, where: {categoryId: ${categoryId}, orderby: {field: DATE, order: DESC}}) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        cursor
        node {
          id
          date
          title
          excerpt(format: RENDERED)
          slug
          author {
            node {
              id
              name
              uri
              slug
              avatar {
                url
              }
            }
          }
          featuredImage {
            node {
              id
              sourceUrl(size: LARGE)
              altText
              mediaDetails {
                height
                width
              }
            }
          }
          seo {
            readingTime
            opengraphUrl
          }
          primaryCategory
        }
      }
    }
  }
  `;

const getAllPostsWithSlug = (first, after) => `query GetAllPostsWithSlug{
    posts(first: ${first}
      after: ${after}) {
        edges {
        node {
          id
          slug
          date
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }`;

const getAllCategoriesWithURI = () => `query GetAllCategories{
  categories(first: 1000) {
    edges {
      node {
        id
        uri
      }
    }
  }
}`;

const getPostByURI = (uri) => `query GetPostByUri {
  postBy(uri: "${uri}") {
    id
    postId
    excerpt
    content(format: RENDERED)
    title(format: RENDERED)
    date
    author {
      node {
        id
        name
        uri
        slug
      }
    }
    paywallMessageText {
      paywallHeader
      paywallSubtitle
    }
    seo {
      fullHead
      opengraphUrl
    }
    slug
    postId
    featuredImage {
      node {
        id
        # Sized variants keep payloads small and isolate pages from a corrupt
        # cache entry on the full-size file; WordPress falls back to the
        # original URL when a variant does not exist (FFH-2340). The article
        # hero renders wider than list thumbnails, so it uses the 1536px core
        # size rather than LARGE (1024px) used by the list queries.
        sourceUrl(size: _1536X1536)
        altText
        mediaDetails {
            height
            width
        }
      }
    }
    seo {
      opengraphUrl
      readingTime
    }
    categories {
      nodes {
        id
        categoryId
        name
      }
    }
  }
}`;

const getCategorySEOByURI = (uri) => `query GetNodeByUri {
  nodeByUri(uri: "${uri}") {
    __typename
    ... on Category {
      seo {
        fullHead
        opengraphUrl
      }
    }
  }
}`;

export {
  client,
  getArticlesByAuthorNameQuery,
  getCategoryBySlugQuery,
  getPostsByCategoryId,
  getPostByURI,
  getAllPostsWithSlug,
  getAllCategoriesWithURI,
  getCategorySEOByURI,
  getTeamRevealsByAuthorNameQuery
};
